import { jsx as _jsx } from "react/jsx-runtime";
import { Children, createContext, isValidElement, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useId } from '../../hooks';
import { findChildRecursively } from '../../utils/findChildRecursively';
import { DECREMENT_ACTIVE_OPTION_INDEX, DISPLAY_NAME_ERROR, DISPLAY_NAME_INPUT, DISPLAY_NAME_OPTION, INCREMENT_ACTIVE_OPTION_INDEX, SELECT_ACTIVE_OPTION, SET_ACTIVE_OPTION_INDEX, SET_INPUT_VALUE, SET_IS_DISABLED, SET_IS_INVALID, SET_IS_NAVIGATING_WITH_KEYBOARD, SET_IS_OPEN, SET_IS_REQUIRED, SET_OPTIONS, SET_SELECTED_VALUE } from './constants';
const DEFAULT_ACTIVE_OPTION_INDEX = -1;
const Context = createContext({
    activeOptionIndex: DEFAULT_ACTIVE_OPTION_INDEX,
    decrementActiveOptionIndex: () => { },
    id: '',
    incrementActiveOptionIndex: () => { },
    inputValue: '',
    isOpen: false,
    isRequired: false,
    isInvalid: false,
    selectActiveOption: () => { },
    selectedValue: null,
    setInputValue: () => { },
    setIsOpen: () => { },
    setSelectedValue: () => { },
    setActiveOptionIndex: () => { },
    setIsNavigatingWithKeyboard: () => { },
    isNavigatingWithKeyboard: false
});
const initialState = {
    activeOptionIndex: DEFAULT_ACTIVE_OPTION_INDEX,
    inputValue: '',
    isDisabled: false,
    isInvalid: false,
    isRequired: false,
    isOpen: false,
    options: [],
    selectedValue: null,
    isNavigatingWithKeyboard: false
};
function reducer(state, action) {
    switch (action.type) {
        case SET_IS_OPEN:
            if (state.isDisabled || state.isOpen === action.payload) {
                return state;
            }
            return {
                ...state,
                activeOptionIndex: !action.payload && state.selectedValue
                    ? state.options.findIndex(option => { var _a; return option.id === ((_a = state.selectedValue) === null || _a === void 0 ? void 0 : _a.id); })
                    : state.activeOptionIndex,
                isOpen: action.payload
            };
        case SET_SELECTED_VALUE: {
            if (!action.payload)
                return state;
            const selectedOptionIndex = state.options.findIndex(option => option.name === action.payload);
            const selectedOption = state.options[selectedOptionIndex] || null;
            return {
                ...state,
                activeOptionIndex: selectedOptionIndex,
                inputValue: action.payload || '',
                selectedValue: selectedOption
            };
        }
        case SET_INPUT_VALUE:
            return {
                ...state,
                inputValue: action.payload
            };
        case INCREMENT_ACTIVE_OPTION_INDEX:
            if (!state.isOpen) {
                return state;
            }
            return {
                ...state,
                activeOptionIndex: Math.min(state.activeOptionIndex + 1, state.options.length - 1),
                isNavigatingWithKeyboard: true
            };
        case DECREMENT_ACTIVE_OPTION_INDEX:
            if (!state.isOpen) {
                return state;
            }
            return {
                ...state,
                activeOptionIndex: Math.max(state.activeOptionIndex - 1, 0),
                isNavigatingWithKeyboard: true
            };
        case SET_OPTIONS:
            return { ...state, options: action.payload };
        case SELECT_ACTIVE_OPTION:
            if (state.activeOptionIndex >= 0 && state.activeOptionIndex < state.options.length) {
                const selectedValue = state.options[state.activeOptionIndex];
                return {
                    ...state,
                    inputValue: selectedValue.name,
                    isOpen: false,
                    selectedValue
                };
            }
            return state;
        case SET_ACTIVE_OPTION_INDEX:
            return {
                ...state,
                activeOptionIndex: action.payload
            };
        case SET_IS_DISABLED:
            return { ...state, isDisabled: action.payload };
        case SET_IS_REQUIRED:
            return { ...state, isRequired: action.payload };
        case SET_IS_INVALID:
            return { ...state, isInvalid: action.payload };
        case SET_IS_NAVIGATING_WITH_KEYBOARD:
            if (state.isNavigatingWithKeyboard === action.payload) {
                return state;
            }
            return {
                ...state,
                isNavigatingWithKeyboard: action.payload
            };
        default:
            return state;
    }
}
function Provider({ id: customId, children, onClose = () => { }, onChange = () => { }, ...rest }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const id = useId(customId);
    const isMounted = useRef(false);
    useEffect(() => {
        if (isMounted.current) {
            if (!state.isOpen) {
                onClose();
            }
        }
        else {
            isMounted.current = true;
        }
    }, [state.isOpen, onClose]);
    useEffect(() => {
        if (isMounted.current) {
            onChange(state.selectedValue);
        }
    }, [state.selectedValue, onChange]);
    useEffect(() => {
        dispatch({ type: SET_IS_INVALID, payload: initialState.isInvalid });
        const options = [];
        const checkChild = (child) => {
            if (isValidElement(child)) {
                const { displayName } = child.type;
                if (displayName === DISPLAY_NAME_OPTION)
                    options.push(child.props.value);
                if (child.props.children) {
                    Children.forEach(child.props.children, checkChild);
                }
                return true;
            }
            return false;
        };
        findChildRecursively(children, checkChild);
        dispatch({ type: SET_OPTIONS, payload: options });
    }, [children]);
    useEffect(() => {
        const checkChild = (child) => {
            if (isValidElement(child)) {
                const { displayName } = child.type;
                if (displayName === DISPLAY_NAME_INPUT) {
                    const { defaultValue, disabled, required } = child.props;
                    if (defaultValue)
                        dispatch({ type: SET_SELECTED_VALUE, payload: defaultValue });
                    if (disabled)
                        dispatch({ type: SET_IS_DISABLED, payload: true });
                    if (required)
                        dispatch({ type: SET_IS_REQUIRED, payload: true });
                }
                if (displayName === DISPLAY_NAME_ERROR) {
                    dispatch({ type: SET_IS_INVALID, payload: true });
                }
                if (child.props.children) {
                    Children.forEach(child.props.children, checkChild);
                }
                return true;
            }
            return false;
        };
        findChildRecursively(children, checkChild);
    }, [children]);
    const decrementActiveOptionIndex = useCallback(() => dispatch({ type: DECREMENT_ACTIVE_OPTION_INDEX }), []);
    const incrementActiveOptionIndex = useCallback(() => dispatch({ type: INCREMENT_ACTIVE_OPTION_INDEX }), []);
    const selectActiveOption = useCallback(() => dispatch({ type: SELECT_ACTIVE_OPTION }), []);
    const setInputValue = useCallback((value) => dispatch({ type: SET_INPUT_VALUE, payload: value }), []);
    const setIsOpen = useCallback((isOpen) => dispatch({ type: SET_IS_OPEN, payload: isOpen }), []);
    const setSelectedValue = useCallback((value) => dispatch({ type: SET_SELECTED_VALUE, payload: value }), []);
    const setActiveOptionIndex = useCallback((index) => dispatch({ type: SET_ACTIVE_OPTION_INDEX, payload: index }), []);
    const setIsNavigatingWithKeyboard = useCallback((isNavigating) => dispatch({ type: SET_IS_NAVIGATING_WITH_KEYBOARD, payload: isNavigating }), []);
    const value = useMemo(() => ({
        decrementActiveOptionIndex,
        id,
        incrementActiveOptionIndex,
        selectActiveOption,
        setInputValue,
        setIsOpen,
        setSelectedValue,
        setActiveOptionIndex,
        setIsNavigatingWithKeyboard,
        ...state
    }), [
        id,
        state,
        decrementActiveOptionIndex,
        incrementActiveOptionIndex,
        selectActiveOption,
        setInputValue,
        setIsOpen,
        setSelectedValue,
        setActiveOptionIndex,
        setIsNavigatingWithKeyboard
    ]);
    return (_jsx(Context.Provider, { value: value, ...rest, children: children }));
}
export { Context, Provider };
//# sourceMappingURL=Context.js.map